import apiService, { ApiError } from './apiService';

/**
 * Merchant Service
 * Handles all merchant-related API operations
 */
class MerchantService {
  /**
   * Format date to API required format (YYYY-MM-DD HH:mm:ss)
   * @param {string|Date} date - Date to format
   * @param {boolean} isEndDate - Whether this is end date (adds 23:59:59)
   * @returns {string} Formatted date string
   */
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    const time = isEndDate ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day} ${time}`;
  }

  /**
   * Get list of merchants with pagination
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20)
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Merchant list response
   */
  async getMerchantList(params = {}, options = {}) {
    const { page = 1, size = 20, keyword, dateFrom, dateTo, order } = params;

    try {
      // Build request data
      const requestData = {
        page,
        size,
      };

      // Add keyword to payload if provided
      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      // Add order if provided
      if (order) {
        requestData.order = order;
      }

      // Add dateFilter to payload if dates are provided
      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiOneRequest('/api/v1/partner/list', {
        method: 'POST',
        data: requestData,
        ...options,
      });

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from merchant list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch merchant list:', error);

      // Re-throw with additional context for merchant-specific errors
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle unexpected errors
      throw new ApiError(
        'Failed to retrieve merchant list',
        0,
        'MERCHANT_LIST_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get merchant details by ID
   * @param {string} merchantId - Merchant ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Merchant details response
   */
  async getMerchantDetails(merchantId, options = {}) {
    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/admin/${merchantId}`,
        {
          method: 'GET',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(
        `Failed to fetch merchant details for ID ${merchantId}:`,
        error
      );

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve merchant details',
        0,
        'MERCHANT_DETAILS_ERROR',
        { merchantId, originalError: error.message }
      );
    }
  }

  /**
   * Export merchant list. Backend processes the CSV asynchronously and sends it
   * to the requesting admin's email.
   * @param {Object} params - Filtering parameters
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {string} params.order - Sort order (optional)
   * @param {string} params.referrerAccountID - Referrer account filter (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Export request response
   */
  async exportMerchantList(params = {}, options = {}) {
    const { keyword, dateFrom, dateTo, order, referrerAccountID } = params;

    try {
      const requestData = {};

      if (order) {
        requestData.order = order;
      }

      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      if (referrerAccountID) {
        requestData.referrerAccountID = referrerAccountID;
      }

      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/list-export',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response?.data || typeof response.data !== 'object') {
        throw new ApiError(
          'Invalid response format from merchant export API. Expected JSON object.',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to export merchant list:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to export merchant list',
        0,
        'MERCHANT_EXPORT_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Search merchants by criteria
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.query - Search query (name, email, etc.)
   * @param {number} searchParams.page - Page number (default: 1)
   * @param {number} searchParams.size - Page size (default: 20)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Search results response
   */
  async searchMerchants(searchParams = {}, options = {}) {
    const { query, page = 1, size = 20 } = searchParams;

    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/search',
        {
          method: 'POST',
          data: {
            query,
            page,
            size,
          },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to search merchants:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to search merchants',
        0,
        'MERCHANT_SEARCH_ERROR',
        { searchParams, originalError: error.message }
      );
    }
  }

  /**
   * Search merchants for admin with keyword
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.keyword - Search keyword
   * @param {number} searchParams.page - Page number (default: 1)
   * @param {number} searchParams.size - Page size (default: 20)
   * @param {string} searchParams.order - Order parameter (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Search results response
   */
  async searchMerchantsAdmin(searchParams = {}, options = {}) {
    const { keyword = '', page = 1, size = 20, order = '' } = searchParams;

    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/search-admin',
        {
          method: 'POST',
          data: {
            keyword,
            page,
            size,
            order,
          },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to search merchants (admin):', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to search merchants (admin)',
        0,
        'MERCHANT_SEARCH_ADMIN_ERROR',
        { searchParams, originalError: error.message }
      );
    }
  }

  /**
   * Transform API merchant data to frontend format
   * @param {Object} apiMerchant - Raw merchant data from API
   * @returns {Object} Transformed merchant data
   */
  transformMerchantData(apiMerchant) {
    if (!apiMerchant) return null;

    return {
      id: apiMerchant.id,
      accountId: apiMerchant.accountId,
      revealed: apiMerchant.revealed === true,
      companyName: apiMerchant.name || 'N/A',
      email: apiMerchant.email || 'N/A',
      phone: apiMerchant.phone || 'N/A',
      address: apiMerchant.address || 'N/A',
      zip: apiMerchant.zip || 'N/A',
      sector: apiMerchant.categoryName || 'N/A',
      categoryId: apiMerchant.categoryId,
      country: apiMerchant.countryName || 'N/A', // This might need country name lookup
      countryId: apiMerchant.countryId,
      // Province & city untuk Business Details (BE sudah resolve dari stateId / cityId)
      province: apiMerchant.stateName ?? '',
      stateId: apiMerchant.stateId,
      city: apiMerchant.cityName ?? '',
      cityId: apiMerchant.cityId,
      // Business Details edit page fields (BE returns them directly on detail GET)
      companyType: apiMerchant.companyType ?? '',
      preferredOption: apiMerchant.preferredOption ?? '',
      website: apiMerchant.website ?? '',
      description: apiMerchant.description ?? apiMerchant.about ?? '',
      companyGooglePlaces: apiMerchant.googlePlaces ?? apiMerchant.companyGooglePlaces ?? '',
      partitaIva: apiMerchant.billingPartitaIva ?? '',
      billingLegalCompanyName: apiMerchant.billingLegalCompanyName ?? '',
      billingAddress: apiMerchant.billingAddress ?? '',
      billingTown: apiMerchant.billingTown ?? '',
      billingProvince: apiMerchant.billingProvince ?? '',
      billingZipCode: apiMerchant.billingZipCode ?? '',
      billingSdiCode: apiMerchant.billingSdiCode ?? '',
      billingPecEmail: apiMerchant.billingPecEmail ?? '',
      status: apiMerchant.status || 'N/A',
      balance: apiMerchant.balance || 0,
      careerPoint: Math.round(apiMerchant.careerPoint || 0),
      joinDate: apiMerchant.createdAt
        ? new Date(apiMerchant.createdAt).toLocaleDateString('en-US')
        : 'N/A',
      lastModified: apiMerchant.modifiedAt
        ? new Date(apiMerchant.modifiedAt).toLocaleDateString('en-US')
        : 'N/A',
      referralCode: apiMerchant.referral || 'N/A',
      referrer: apiMerchant.referrer,
      referrerAccountId: apiMerchant.referrerAccountId,
      downline: apiMerchant.downline || 0,
      partner: apiMerchant.partner || 0,
      paidStart: apiMerchant.paidStart,
      paidExpired: apiMerchant.paidExpired,
      headQuarter: apiMerchant.headQuarter,
      googleID: apiMerchant.googleID,
      voucher: apiMerchant.voucher,
      recommenderName: apiMerchant.recommenderName || '',
      emailVerified: apiMerchant.emailVerified,
      phoneVerified: apiMerchant.phoneVerified,
      marginId: apiMerchant.marginId,
      marketingBoost: apiMerchant.marketingBoost || 0,
      marketingBonusBalance: apiMerchant.marketingBonusBalance ?? 0,
      marketingFeePercent: apiMerchant.marketingFeePercent ?? null,
      lastPlatformFeePaymentAt: apiMerchant.lastPlatformFeePaymentAt,
      lastPlatformFeePaymentDate: apiMerchant.lastPlatformFeePaymentAt
        ? new Date(apiMerchant.lastPlatformFeePaymentAt).toLocaleDateString(
            'en-US'
          )
        : '',
      lastSubscriptionPaymentAt: apiMerchant.lastSubscriptionPaymentAt,
      lastSubscriptionPaymentDate: apiMerchant.lastSubscriptionPaymentAt
        ? new Date(apiMerchant.lastSubscriptionPaymentAt).toLocaleDateString(
            'en-US'
          )
        : '',
      lat: apiMerchant.lat || 0,
      lon: apiMerchant.lon || 0,
      // Business Owner (BE menyediakan field owner* terpisah dari kontak merchant)
      ownerFullName: apiMerchant.ownerFullName ?? '',
      ownerEmail: apiMerchant.ownerEmail ?? '',
      ownerPhone: apiMerchant.ownerPhone ?? '',
      ownerJoinDate: apiMerchant.ownerJoinDate
        ? new Date(apiMerchant.ownerJoinDate).toLocaleDateString('en-US')
        : '',
      // Add computed fields
      isActive: apiMerchant.status === 'A',
      isPaid:
        apiMerchant.paidExpired &&
        new Date(apiMerchant.paidExpired) > new Date(),
      hasBalance: (apiMerchant.balance || 0) > 0,
      // A negative marketing balance hides the merchant from the SKAi1 app:
      // the shopper partners / snap-earn-near-me queries filter on
      // `balance >= 0 OR balance IS NULL`. The account status stays 'A', so
      // this is deliberately separate from isActive.
      hasNegativeBalance: Number(apiMerchant.balance ?? 0) < 0,
      // Subscription validity, grace period included, as computed by BE and
      // shipped on the detail endpoint (`plus`). False also covers merchants
      // that never subscribed at all (zero-value paidExpired). The list
      // endpoint does NOT send it, so keep it null there rather than
      // defaulting to false — otherwise list-sourced data would read as an
      // expired subscription.
      subscriptionActive:
        typeof apiMerchant.plus === 'boolean' ? apiMerchant.plus : null,
      // Communication consent from `advisorConsent.enabled`. Only the detail
      // endpoint fills it — the list endpoint sends `advisorConsent: null` —
      // so keep it null there instead of reading it as consent withdrawn.
      communicationConsent:
        typeof apiMerchant.advisorConsent?.enabled === 'boolean'
          ? apiMerchant.advisorConsent.enabled
          : null,
      createdAt: apiMerchant.createdAt,
      modifiedAt: apiMerchant.modifiedAt,
      modifiedBy: apiMerchant.modifiedBy,
      modifiedByName: apiMerchant.modifiedByName,
    };
  }

  /**
   * Get formatted merchant list with transformed data
   * @param {Object} params - Pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20)
   * @param {string} params.keyword - Search keyword (optional)
   * @param {string} params.dateFrom - Start date for filtering (optional)
   * @param {string} params.dateTo - End date for filtering (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted merchant list response
   */
  async getFormattedMerchantList(params = {}, options = {}) {
    try {
      console.log('Fetching merchants with params:', params);
      const response = await this.getMerchantList(params, options);

      // Transform the merchant data
      const transformedData = {
        ...response.data,
        data: response.data.data.map(merchant =>
          this.transformMerchantData(merchant)
        ),
      };

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Failed to get formatted merchant list:', error);
      throw error;
    }
  }

  /**
   * Get formatted merchant details by ID
   * @param {string} merchantId - Merchant ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Formatted merchant details
   */
  async getFormattedMerchantDetails(merchantId, options = {}) {
    try {
      const response = await this.getMerchantDetails(merchantId, options);

      // Transform the merchant data
      const transformedData = this.transformMerchantData(response.data);

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error(
        `Failed to get formatted merchant details for ID ${merchantId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Create a new merchant
   * @param {Object} merchantData - Merchant data to create
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Created merchant response
   */
  async createMerchant(merchantData, options = {}) {
    try {
      const response = await apiService.skaiOneRequest('/api/v1/partner', {
        method: 'POST',
        data: merchantData,
        ...options,
      });

      return response;
    } catch (error) {
      console.error('Failed to create merchant:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to create merchant',
        0,
        'MERCHANT_CREATE_ERROR',
        { merchantData, originalError: error.message }
      );
    }
  }

  /**
   * Update merchant information
   * @param {string} merchantId - Merchant ID
   * @param {Object} updateData - Data to update
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Updated merchant response
   */
  async updateMerchant(merchantId, updateData, options = {}) {
    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/${merchantId}`,
        {
          method: 'PUT',
          data: updateData,
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to update merchant ${merchantId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to update merchant',
        0,
        'MERCHANT_UPDATE_ERROR',
        { merchantId, updateData, originalError: error.message }
      );
    }
  }

  /**
   * Update partner billing details (Italian invoicing fields)
   * @param {string} partnerId - Partner / merchant ID
   * @param {Object} billingData - Billing payload
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} API response
   */
  async updatePartnerBilling(partnerId, billingData, options = {}) {
    if (!partnerId) {
      throw new ApiError('Partner ID is required', 400, 'MISSING_PARTNER_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/${partnerId}/billing`,
        {
          method: 'PUT',
          data: billingData,
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to update partner billing ${partnerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to update billing details',
        0,
        'PARTNER_BILLING_UPDATE_ERROR',
        { partnerId, billingData, originalError: error.message }
      );
    }
  }

  /**
   * Validate Partita IVA and retrieve suggested billing data
   * @param {string} partnerId - Partner / merchant ID
   * @param {string} partitaIva - Partita IVA to validate
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} API response with suggested billing data
   */
  async validatePartitaIva(partnerId, partitaIva, options = {}) {
    if (!partnerId) {
      throw new ApiError('Partner ID is required', 400, 'MISSING_PARTNER_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/${partnerId}/billing/validate`,
        {
          method: 'POST',
          data: { partitaIva },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to validate Partita IVA for partner ${partnerId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to validate Partita IVA',
        0,
        'PARTITA_IVA_VALIDATE_ERROR',
        { partnerId, originalError: error.message }
      );
    }
  }

  /**
   * Update merchant business owner (admin) — full update of name/phone/email.
   * Endpoint: PUT /api/v1/partner/admin/:id/business-owner
   *
   * Per release notes: this is NOT a partial update — caller must always send
   * all three of `name`, `phone`, `email` (even unchanged values). Resolves
   * the target account via `company_users` (role=owner) or `company.account_id`.
   *
   * `skipVerificationReset` (optional bool) preserves `phone_verified` /
   * `email_verified` flags when phone/email change.
   */
  async updateBusinessOwner(merchantId, payload, options = {}) {
    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }
    const trim = v => String(v ?? '').trim();
    const body = {
      name: trim(payload?.name),
      phone: trim(payload?.phone),
      email: trim(payload?.email),
      skipVerificationReset: payload?.skipVerificationReset === true,
    };
    const required = ['name', 'phone', 'email'];
    for (const key of required) {
      if (!body[key]) {
        throw new ApiError(
          `${key} is required`,
          400,
          'FeatureInvalidPayload'
        );
      }
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/admin/${merchantId}/business-owner`,
        {
          method: 'PUT',
          data: body,
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to update business owner for merchant ${merchantId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update business owner',
        0,
        'BUSINESS_OWNER_UPDATE_ERROR',
        { merchantId, originalError: error.message }
      );
    }
  }

  /**
   * Update merchant business details (admin) — full update.
   * Endpoint: PUT /api/v1/partner/admin/:id/business-details
   *
   * Required: companyName, companyCategory, companyAddress, companyCountry,
   * companyCity, companyZip, companyPhone (E.164), companyEmail.
   * Optional fields omitted if empty/null.
   *
   * `description` (Business Summary) is optional and always sent — `""` clears
   * it, an absent key or `null` keeps the stored value. Needs skai-one branch
   * `feat/merchant-business-summary-optional`; against older BE, an empty
   * Business Summary returns 400 "Field Description required".
   */
  async updateBusinessDetails(merchantId, payload, options = {}) {
    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }
    const trim = v => String(v ?? '').trim();
    const required = {
      companyName: trim(payload?.companyName),
      companyCategory: trim(payload?.companyCategory),
      companyAddress: trim(payload?.companyAddress),
      companyCountry: trim(payload?.companyCountry),
      companyCity: trim(payload?.companyCity),
      companyZip: trim(payload?.companyZip),
      companyPhone: trim(payload?.companyPhone),
      companyEmail: trim(payload?.companyEmail),
    };
    for (const [key, value] of Object.entries(required)) {
      if (!value) {
        throw new ApiError(
          `${key} is required`,
          400,
          'FeatureInvalidPayload'
        );
      }
    }
    // Optional fields — include only if non-empty so BE keeps existing values
    // untouched. Booleans/enums sent as-is when provided.
    const optional = {};
    const optionalKeys = [
      'companyType',
      'preferredOption',
      'companyCategoryId',
      'companyLat',
      'companyLon',
      'companyCountryId',
      'companyState',
      'companyStateId',
      'companyCityId',
      'website',
      'companyGoogle',
      'companyGooglePlaces',
    ];
    for (const key of optionalKeys) {
      const value = payload?.[key];
      if (value === undefined || value === null) continue;
      const str = String(value).trim();
      if (str) optional[key] = str;
    }
    // `description` (Business Summary) is optional but must ALWAYS be sent, so
    // it is set after the spreads rather than living in `optionalKeys` (that
    // loop drops empty values). BE contract: `""` clears the column, while an
    // absent key — and `null` — mean "keep the existing value", so omitting it
    // would make clearing impossible. BE trims, so the raw form value goes out
    // as-is; `null`/`undefined` collapse to `''` because the only caller always
    // supplies the field from form state.
    const body = {
      ...required,
      ...optional,
      description: String(payload?.description ?? ''),
    };

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/admin/${merchantId}/business-details`,
        {
          method: 'PUT',
          data: body,
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to update business details for merchant ${merchantId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update business details',
        0,
        'BUSINESS_DETAILS_UPDATE_ERROR',
        { merchantId, originalError: error.message }
      );
    }
  }

  /**
   * Get parent categories (Sector dropdown). Mirrors the onboarding flow's
   * categoryService.getParent — returns `{ data: [...] }` of `{ id, name }`.
   */
  async getCategoriesParent(options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/category/parent',
        { method: 'GET', ...options }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch parent categories:', error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to fetch categories',
        0,
        'CATEGORY_FETCH_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get state/province list for a country (cascading dropdown).
   * Mirrors onboarding `geoService.getState(countryCode)`.
   */
  async getStates(countryCode, options = {}) {
    if (!countryCode) return { data: { list: [] } };
    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/geo/state?country_code=${encodeURIComponent(countryCode)}`,
        { method: 'GET', ...options }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch states:', error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to fetch states',
        0,
        'STATE_FETCH_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get city list for a country+state (cascading dropdown).
   * Mirrors onboarding `geoService.getCity(countryCode, stateCode)`.
   */
  async getCities(countryCode, stateCode, options = {}) {
    if (!countryCode || !stateCode) return { data: { list: [] } };
    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/geo/city?country_code=${encodeURIComponent(
          countryCode
        )}&state_code=${encodeURIComponent(stateCode)}`,
        { method: 'GET', ...options }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch cities:', error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to fetch cities',
        0,
        'CITY_FETCH_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get country list (Country dropdown). Mirrors onboarding `geoService.getCountry`.
   * Returns rows of `{ id, code, name, phoneCode }`.
   */
  async getCountries(options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/geo/country',
        { method: 'GET', ...options }
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to fetch countries',
        0,
        'COUNTRY_FETCH_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Delete/deactivate a merchant
   * @param {string} merchantId - Merchant ID
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Delete response
   */
  async deleteMerchant(merchantId, options = {}) {
    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/partner/${merchantId}`,
        {
          method: 'DELETE',
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to delete merchant ${merchantId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to delete merchant',
        0,
        'MERCHANT_DELETE_ERROR',
        { merchantId, originalError: error.message }
      );
    }
  }

  /**
   * Top up merchant balance
   * @param {Object} topupData - Top-up data
   * @param {string} topupData.merchantId - Merchant ID to top up
   * @param {number} topupData.amount - Amount to top up
   * @param {string} topupData.currency - Currency (default: 'EUR')
   * @param {string} topupData.reference - Reference note for the top-up
   * @param {boolean} topupData.confirm - Confirm the top-up (default: true)
   * @param {boolean} topupData.preview - Preview mode (default: false)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Top-up response
   */
  async topupBalance(topupData, options = {}) {
    const {
      merchantId,
      amount,
      currency = 'EUR',
      reference,
      confirm = true,
      preview = false,
    } = topupData;

    if (!merchantId) {
      throw new ApiError('Merchant ID is required', 400, 'MISSING_MERCHANT_ID');
    }

    if (!amount || amount <= 0) {
      throw new ApiError(
        'Valid positive amount is required',
        400,
        'INVALID_AMOUNT'
      );
    }

    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/topup',
        {
          method: 'POST',
          data: {
            merchantId,
            amount,
            currency,
            reference,
            confirm,
            preview,
          },
          ...options,
        }
      );

      return response;
    } catch (error) {
      console.error(`Failed to top up merchant ${merchantId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to top up merchant balance',
        0,
        'MERCHANT_TOPUP_ERROR',
        { topupData, originalError: error.message }
      );
    }
  }

  /**
   * Get balance history for a merchant
   * @param {Object} params - Balance history parameters
   * @param {string} params.companyId - Company/Merchant ID (optional)
   * @param {string} params.type - Transaction type (default: 'N')
   * @param {string} params.startDate - Start date in YYYY-MM-DD format (optional)
   * @param {string} params.endDate - End date in YYYY-MM-DD format (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Balance history response
   */
  async balanceHistory(params = {}, options = {}) {
    const {
      keyword = '',
      type = 'N',
      startDate,
      endDate,
      page = 1,
      size = 20,
    } = params;

    try {
      const requestData = {
        keyword,
        type,
        page,
        size,
      };

      if (startDate) {
        requestData.startDate = startDate;
      }

      if (endDate) {
        requestData.endDate = endDate;
      }

      const response = await apiService.skaiOneRequest(
        '/api/v1/partner/balance/history',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from balance history API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch balance history:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve balance history',
        0,
        'BALANCE_HISTORY_ERROR',
        { params, originalError: error.message }
      );
    }
  }
}

// Create singleton instance
const merchantService = new MerchantService();

export default merchantService;
export { MerchantService };
